import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineLatest, Observable, ReplaySubject, Subject, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ContactpersonalOverviewModel, CustomerOverviewModel, GenericPostRequest, PaginationPage, PaginationResponse, ResponseWrapper } from 'src/app/classes';
import { serverLocation } from 'src/app/globals';
import { ContractItem } from '../../contract/classes';
import { Offer } from '../../offer/classes';
import { TokenService } from '../../shared/services/token.service';
import { Contact, Customer } from '../classes';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {

  private _customers: { [key: number]: ReplaySubject<Customer> } = {};
  customers$ = combineLatest(Object.values(this._customers));

  constructor(
    private _httpClient: HttpClient,
    private _tokenService: TokenService
  ) { }

  checkCustomerVersion(customer: Customer): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/customer/${customer.customerid}/check-version/${customer.concurrencystamp}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  clearCustomerHistory = () => this._httpClient.delete(`${serverLocation}/api/customer/history`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  });

  createCustomer(customer: Customer, auditNote: string): Observable<Customer> {
    return this._httpClient.post(`${serverLocation}/api/customer`, {
      note: auditNote,
      content: customer
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Customer>;
  }

  customerIsWatched(customerId: number): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/customer/${customerId}/watched`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  deleteCustomer(customerId: number, request): Observable<Object> {
    let subject = new Subject();
    this._deleteCustomerById(customerId, request).pipe(tap(() => {
      if (this._customers[customerId]) {
        this._customers[customerId].complete();
        delete this._customers[customerId];
      }
    })).subscribe(subject);
    return subject;
  }

  getCustomerById(identifier: number, soft: boolean = true): Observable<Customer> {
    if (typeof identifier !== 'number') return throwError('no valid customer identifier passed');
    if (!soft || !this._customers[identifier]) {
      if (!this._customers[identifier]) this._customers[identifier] = new ReplaySubject(1);
      this._getCustomerById(identifier).subscribe({
        next: (customer: Customer) => this._customers[identifier].next(customer),
        error: (error: HttpErrorResponse) => this._customers[identifier].error(error)
      });
    }
    return this._customers[identifier].asObservable();
  }

  getCustomerContacts(customerId: number): Observable<Contact[]> {
    return this._httpClient.get(`${serverLocation}/api/customer/${customerId}/contacts`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Contact[]>;
  }

  getCustomerContractItems(identifier: number): Observable<ContractItem[]> {
    return this._httpClient.get(`${serverLocation}/api/customer/${identifier}/contract-items`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ContractItem[]>;
  }

  getCustomerOffers(customerId: number): Observable<Offer[]> {
    return this._httpClient.get(`${serverLocation}/api/customer/${customerId}/offers`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Offer[]>;
  }

  getNextCustomerNumber(): Observable<string> {
    return this._httpClient.get(`${serverLocation}/api/customer/next-number`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<string>;
  }

  getUserCustomerHistory(page: PaginationPage) {
    return this._httpClient.post(`${serverLocation}/api/customer/history`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<CustomerOverviewModel[]>>;
  }

  getUserCustomerFavourites(page: PaginationPage) {
    return this._httpClient.post(`${serverLocation}/api/customer/favourites`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<CustomerOverviewModel[]>>;
  }

  
  linkContact(customerId: number, contactId: number): Observable<Object> {
    return this._httpClient.post(`${serverLocation}/api/customer/contactpersonal/add`, {
      customer: customerId,
      contactpersonal: contactId
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ContactpersonalOverviewModel[]>;
  }

  saveHistory(customerId: number): Observable<Object> {
    let subject = new Subject();
    this._httpClient.get(`${serverLocation}/api/customer/history/${customerId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }).subscribe(subject);
    return subject.asObservable();
  }

  searchCustomersByString(search: string, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/customer/search`, {
      'searchString': search,
      'page': paginationPage
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<CustomerOverviewModel[]>>;
  }

  searchCustomersByUserFilter(identifier: number, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/customer/user-filter-search/${identifier}`, paginationPage, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<CustomerOverviewModel[]>>;
  }

  setCustomerWatchedStatus(issueId: number, watch: boolean): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/customer/${issueId}/${watch? 'watch': 'unwatch'}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  unlinkContact(customerId: number, contactId: number): Observable<Object> {
    return this._httpClient.post(`${serverLocation}/api/customer/contactpersonal/delete`, {
      customer: customerId,
      contactpersonal: contactId
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ContactpersonalOverviewModel[]>;
  }

  updateCustomer(customer: Customer, note: string = null): Observable<Customer> {
    return this._httpClient.put(`${serverLocation}/api/customer`, {
      content: customer,
      note: note
    } as GenericPostRequest<Customer>, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Customer>;
  }

  private _deleteCustomerById(customerId: number, request: { deleteIssues: boolean, deleteContracts: boolean } = { deleteIssues: false, deleteContracts: false }): Observable<Object> {
    return this._httpClient.request('delete', `${serverLocation}/api/customer/${customerId}`, {
      body: request,
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  private _getCustomerById(identifier: number): Observable<Customer> {
    return this._httpClient.get(`${serverLocation}/api/customer/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Customer>;
  }

}
